import { memo, useCallback, useContext, useMemo } from 'react';
import { PlusCircle } from 'react-feather';
import type { CellContext } from '@tanstack/react-table';
import type { ProjectClips } from '@trinity/chat/types';
import { SetTaggedMomentEditParamsContext } from '../../contexts/tagged-moment/Context';
import { ProjectClipsFiltersContext } from '../../contexts/project-clips/Context';
import { TagBadgeMenu } from './TagBadgeMenu';
import { TagBadge } from './TagBadge';
import styles from './style/Cell.Tags.module.css';
import { cx } from '../../utils';
import { VisibleOnHoverOverlay } from '../Editable';

type Props = CellContext<ProjectClips.Item, unknown> & {
  /** If provided, show delete affordance on each tag badge */
  onDeleteTag?: (tag: ProjectClips.Tag) => () => void;
};

export const TagsCell = ({ row, onDeleteTag }: Props) => {
  const buildTagContext = useContext(SetTaggedMomentEditParamsContext);
  const filters = useContext(ProjectClipsFiltersContext);

  const tagsWithCounts = useMemo(() => {
    const map: Record<number, { count: number; tag: ProjectClips.Tag }> = {};
    for (const tag of row.original.tags) {
      const count = (map[tag.id]?.count ?? 0) + 1;
      map[tag.id] = {
        count,
        tag: { ...tag, name: count > 1 ? `${tag.name} (${count})` : tag.name },
      };
    }
    return Object.values(map)
      .map((m) => m.tag)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [row.original.tags]);

  const handleAddTag = useCallback(() => {
    buildTagContext?.({
      keyword: filters.keyword,
      momentId: null,
      tagId: null,
      range: row.original.range,
      transcriptId: row.original.transcript.id,
    });
  }, [buildTagContext, filters.keyword, row.original]);

  const handleEditTag = useCallback(
    (tag: ProjectClips.Tag) => () => {
      buildTagContext?.({
        keyword: filters.keyword,
        momentId: tag.moment.id,
        tagId: tag.id,
        range: row.original.range,
        transcriptId: row.original.transcript.id,
      });
    },
    [buildTagContext, filters.keyword, row.original]
  );

  const canEdit = !!buildTagContext;
  const canDelete = !!onDeleteTag;

  const className = cx(styles.root, canEdit ? styles.editable : undefined);

  return (
    <VisibleOnHoverOverlay enabled={canEdit} Component={AddTagIcon} onClick={handleAddTag}>
      <div className={className}>
        {tagsWithCounts.map((tag, index) => {
          if (canEdit || canDelete) {
            return (
              <TagBadgeMenu
                key={index}
                tag={tag}
                onEdit={handleEditTag(tag)}
                onDelete={onDeleteTag ? onDeleteTag(tag) : () => void 0}
              />
            );
          }
          return <TagBadge key={index} tag={tag} />;
        })}
        {!tagsWithCounts.length && canEdit && (
          <div className={styles.addTags} onClick={handleAddTag}>
            <PlusCircle size={18} />
            Add Tag
          </div>
        )}
      </div>
    </VisibleOnHoverOverlay>
  );
};

type IconProps = {
  className?: string;
  onClick: () => void;
};
const AddTagIcon = memo(({ className, onClick }: IconProps) => {
  return <PlusCircle onClick={onClick} className={cx(styles.plus, className)} size={18} />;
});

TagsCell.displayName = 'ProjectClips.TagsCell';
